import type React from 'react';
import { useState, useCallback } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

type ViewMode = 'gallery' | 'list';

function getStoredViewMode(): ViewMode {
  return (localStorage.getItem('canvasViewMode') as ViewMode) || 'gallery';
}

export function useViewMode(): [ViewMode, () => void] {
  const [mode, setMode] = useState<ViewMode>(getStoredViewMode);
  const toggle = useCallback(() => {
    setMode((prev) => {
      const next = prev === 'gallery' ? 'list' : 'gallery';
      localStorage.setItem('canvasViewMode', next);
      return next;
    });
  }, []);
  return [mode, toggle];
}

export function ViewToggle({ mode, onToggle }: { mode: ViewMode; onToggle: () => void }) {
  return (
    <button type="button" className="btn view-toggle" onClick={onToggle} title={mode === 'gallery' ? 'Switch to List Mode' : 'Switch to Gallery Mode'}>
      {mode === 'gallery' ? 'List Mode' : 'Gallery Mode'}
    </button>
  );
}

export function Layout() {
  const { user, logout } = useAuth();

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <Link to="/" className="brand">
          <img className="brand-mark" src="/favicon.png" alt="Lion Studios" />
          <strong>Lion Canvas</strong>
        </Link>
        {user && user.role !== 'external' && (
          <>
            <nav className="nav">
              <NavLink to="/" end>My Uploads</NavLink>
              <NavLink to="/shared">Shared with Me</NavLink>
              <NavLink to="/browse">Browse</NavLink>
              <NavLink to="/new">New Upload</NavLink>
              {user.role === 'admin' && <NavLink to="/admin">Admin</NavLink>}
            </nav>
            <div className="nav nav-secondary">
              <NavLink to="/asset-archive">Asset Archive</NavLink>
            </div>
          </>
        )}
        {user && (
          <div className="user-menu">
            <span className="user-name">{user.displayName}</span>
            <button type="button" className="btn ghost" onClick={logout}>Sign out</button>
          </div>
        )}
      </aside>
      <main className="content">
        <Outlet />
      </main>
    </div>
  );
}

export function Skeleton({ width = '100%', height = '1rem', radius = '6px', style }: {
  width?: string; height?: string; radius?: string; style?: React.CSSProperties;
}) {
  return <div className="skeleton" style={{ width, height, borderRadius: radius, ...style }} />;
}

export function SkeletonCard({ viewMode = 'gallery' }: { viewMode?: ViewMode }) {
  if (viewMode === 'list') {
    return (
      <div className="card dashboard-card dashboard-card-list skeleton-card">
        <Skeleton width="160px" height="160px" radius="0" style={{ flexShrink: 0 }} />
        <div className="card-body">
          <Skeleton width="60%" height="1.1rem" style={{ marginBottom: '0.5rem' }} />
          <Skeleton width="35%" height="0.8rem" />
        </div>
      </div>
    );
  }
  return (
    <div className="card dashboard-card skeleton-card">
      <Skeleton height="0" radius="0" style={{ paddingBottom: '100%' }} />
      <div className="card-body">
        <Skeleton width="70%" height="1.1rem" style={{ marginBottom: '0.5rem' }} />
        <Skeleton width="45%" height="0.8rem" />
      </div>
    </div>
  );
}

// Deterministic two-tone gradient + monogram so every card gets a stable,
// on-theme "preview" even though dashboards have no stored thumbnail.
function hashSeed(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

function initials(title: string): string {
  const words = title.trim().split(/\s+/).filter(Boolean);
  return ((words[0]?.[0] ?? '?') + (words[1]?.[0] ?? '')).toUpperCase();
}

export function CardPreview({ seed, title, overlay, screenshotUrl, info }: {
  seed: string;
  title: string;
  overlay?: React.ReactNode;
  screenshotUrl?: string | null;
  info?: React.ReactNode;
}) {
  const hue = hashSeed(seed) % 360;
  const gradientStyle: React.CSSProperties = {
    backgroundImage: `linear-gradient(135deg, hsl(${hue} 42% 24%), hsl(${(hue + 45) % 360} 50% 13%))`,
  };
  return (
    <div className={`card-preview${screenshotUrl ? ' card-preview-screenshot' : ''}`} style={screenshotUrl ? undefined : gradientStyle}>
      {screenshotUrl
        ? <img src={screenshotUrl} alt={title} className="card-preview-img" />
        : <span className="card-preview-monogram">{initials(title)}</span>}
      {overlay && <div className="card-preview-overlay">{overlay}</div>}
      {info && <div className="card-preview-info">{info}</div>}
    </div>
  );
}

export function DashboardCard({
  dashboard,
  linkPrefix = '/dashboard',
  viewMode = 'gallery',
}: {
  dashboard: { dashboardId: string; title: string; status: string; updatedAt: string; tags?: string[]; screenshotUrl?: string | null };
  linkPrefix?: string;
  viewMode?: ViewMode;
}) {
  const overlay = dashboard.status !== 'active'
    ? <span className={`badge status-${dashboard.status}`}>{dashboard.status}</span>
    : undefined;

  const galleryInfo = (
    <>
      <span className="card-preview-info-title">{dashboard.title}</span>
      {(dashboard.tags ?? []).length > 0 && (
        <div className="tag-chips">
          {(dashboard.tags ?? []).map((t) => <span key={t} className="chip">{t}</span>)}
        </div>
      )}
      <span className="card-preview-info-date">Added {new Date(dashboard.updatedAt).toLocaleDateString()}</span>
    </>
  );

  if (viewMode === 'list') {
    return (
      <Link to={`${linkPrefix}/${dashboard.dashboardId}`} className="card dashboard-card dashboard-card-list">
        <CardPreview seed={dashboard.dashboardId} title={dashboard.title} screenshotUrl={dashboard.screenshotUrl} overlay={overlay} />
        <div className="card-body">
          <h3>{dashboard.title}</h3>
          {(dashboard.tags ?? []).length > 0 && (
            <div className="tag-chips">
              {(dashboard.tags ?? []).map((t) => <span key={t} className="chip">{t}</span>)}
            </div>
          )}
          <p className="muted card-date">Added {new Date(dashboard.updatedAt).toLocaleDateString()}</p>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`${linkPrefix}/${dashboard.dashboardId}`} className="card dashboard-card">
      <CardPreview seed={dashboard.dashboardId} title={dashboard.title} screenshotUrl={dashboard.screenshotUrl} overlay={overlay} info={galleryInfo} />
    </Link>
  );
}
